import {PublicationContext} from 'centrifuge';
import {camelizeKeys, decamelizeKeys} from 'humps';
import {noop} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {z as r} from 'zod';

import {useCentrifugeSubscription} from '../../../../../../../provider/centrifuge/centrifuge-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {useSnackbar} from '../../../../../../../provider/snackbar/snackbar-hook';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../../../../../../../service/api/api-const';
import {FetchMethodEnum, fetchNoContent} from '../../../../../../../util/fetch';

const reviewAiSuggestsSchema = r.object({
    error: r.string().nullable(),
    requestId: r.string(),
    stop: r.boolean(),
    text: r.string(),
});

type ReviewAiSuggestType = r.infer<typeof reviewAiSuggestsSchema>;

function requestReviewSuggest(options: {reviewId: number; channelId: string; requestId: string}): Promise<void> {
    return fetchNoContent(`${rootApiUrl}/cp/neuronetwork_replies/create_suggestion/`, {
        method: FetchMethodEnum.post,
        headers: {...mainApiHeaders, ...getCsrfHeaders()},
        body: JSON.stringify(decamelizeKeys(options)),
    });
}

function isReviewSuggestAction(data: unknown): data is ReviewAiSuggestType {
    return reviewAiSuggestsSchema.safeParse(data).success;
}

function getCentrifugeReviewSuggestChannel(options: {channelId: string}) {
    return `review_suggestions:suggest_${options.channelId}`;
}

type ReviewAiSuggestHookType = {
    requestSuggest: (reviewId: number) => void;
    isGeneratingForReview: (reviewId: number) => boolean;
    isBulkGenerate: boolean;
};

export function useNewReviewAiSuggest(
    setAnswerText: (value: string, reviewPk: number) => void
): ReviewAiSuggestHookType {
    const [isGeneratingSuggest, setIsGeneratingSuggest] = useState<boolean>(false);
    const {getSubscription} = useCentrifugeSubscription();
    const {snackbar} = useSnackbar();

    const reviewsGenerateInfoRef = useRef<Array<{channelId: string; reviewId: number; isGenerating: boolean}>>([]);

    const updateGeneratingStatus = useCallback((reviewId: number, isGenerating: boolean) => {
        reviewsGenerateInfoRef.current = reviewsGenerateInfoRef.current.map((item) =>
            item.reviewId === reviewId ? {...item, isGenerating} : item
        );
    }, []);

    const handleSuggestError = useCallback(() => {
        setIsGeneratingSuggest(false);
        snackbar.error({
            message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
            description: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />,
        });
    }, [snackbar]);

    useEffect(() => {
        if (!isGeneratingSuggest) {
            return noop;
        }

        const timeoutId = setTimeout(handleSuggestError, 120_000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [handleSuggestError, isGeneratingSuggest]);

    const requestSuggest = useCallback(
        (reviewId: number) => {
            const channelId = uuidv4();

            const subscription = getSubscription(getCentrifugeReviewSuggestChannel({channelId}));

            function onActionReceived({data}: PublicationContext) {
                const camelcaseData = camelizeKeys(data);

                if (!isReviewSuggestAction(camelcaseData)) {
                    return;
                }

                const generateItem = reviewsGenerateInfoRef.current.find(
                    (item) => item.channelId === camelcaseData.requestId
                );

                if (camelcaseData.stop) {
                    updateGeneratingStatus(reviewId, false);
                    setIsGeneratingSuggest(false);
                }

                if (generateItem?.reviewId) {
                    setAnswerText(camelcaseData.text, generateItem.reviewId);
                }
            }

            subscription?.on('publication', onActionReceived);

            function cleanup() {
                subscription?.removeListener('publication', onActionReceived);
            }

            // Очищаем старые завершенные генерации для этого reviewId
            reviewsGenerateInfoRef.current = reviewsGenerateInfoRef.current.filter(
                (item) => item.reviewId !== reviewId || item.isGenerating
            );

            reviewsGenerateInfoRef.current.push({reviewId, channelId, isGenerating: true});
            setIsGeneratingSuggest(true);

            requestReviewSuggest({reviewId, channelId, requestId: channelId}).catch(() => {
                cleanup();
                handleSuggestError();
            });

            setTimeout(cleanup, 2 * 60 * 1000);
        },
        [getSubscription, updateGeneratingStatus, setAnswerText, handleSuggestError]
    );

    const isGeneratingForReview = useCallback((reviewId: number) => {
        return reviewsGenerateInfoRef.current.some((item) => item.reviewId === reviewId && item.isGenerating);
    }, []);

    const isBulkGenerate = reviewsGenerateInfoRef.current.some((item) => item.isGenerating);

    return {
        requestSuggest,
        isGeneratingForReview,
        isBulkGenerate,
    };
}
